import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { useEffect, useMemo, useState } from "react";
import { Formik, ErrorMessage } from "formik";
import { ArrowRightIcon } from "lucide-react";
import { InfoIcon } from "@/components/Icons/InfoIcon";
import { AlertDescription } from "@/components/ui/alert";
import { CubeIcon } from "@/components/Icons/CubeIcon";
import { CreditCardPlusIcon } from "@/components/Icons/CreditCardPlusIcon";
import { PassportIcon } from "@/components/Icons/PassportIcon";
import * as Yup from "yup";
import { REQUIRED_MESSAGE } from "@/utils/form";
import { useNotificationStore } from "@/store/notification";
import { useToast } from "@/components/ui/use-toast";
import { AnalyticsEventType } from "@/constants/analytics";
import { trackEvent } from "@/utils/analytics";
import { useUserStore } from "@/store/user";
import { RadioCardField, TextField } from "@/components/forms/fields";
import { PayIcon } from "@/components/Icons/PayIcon";
import { VisaIcon } from "@/components/Icons/VisaIcon";
import { MasterCardIcon } from "@/components/Icons/MasterCardIcon";
import ENV_VARIABLES from "@/env";
import { AmericanExpressIcon } from "@/components/Icons/AmericanExpressIcon";
import { PAYMENT_LOADING_HTML } from "@/constants";

export type PurchaseCreditsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  className?: string;
};

export const PurchaseCreditsModal: React.FC<PurchaseCreditsModalProps> = ({ isOpen, onClose }) => {
  const topUpUserCredits = useUserStore((state) => state.topUpUserCredits);
  const initialValues = useMemo(() => {
    return {
      name: "",
      reason: "",
      amount: undefined,
      currency: "usdc"
    } as unknown as PurchaseCreditsFormType;
  }, []);
  const displayError = useNotificationStore((state) => state.displayError);
  const { toast } = useToast();
  const [shouldPollBalance, setShouldPollBalance] = useState(false);
  const fetchBalances = useUserStore((state) => state.fetchBalances);
  // const [paymentLink, setPaymentLink] = useState<string>();

  useEffect(() => {
    if (!shouldPollBalance) {
      return;
    }

    const timeout = setInterval(async () => {
      await fetchBalances(true);
    }, 5000);

    return () => {
      clearInterval(timeout);
    };
  }, [shouldPollBalance, fetchBalances]);

  return (
    <Dialog open={isOpen} modal defaultOpen={isOpen}>
      <DialogContent
        className={cn(
          "custom-scrollbar max-h-[100vh] w-full gap-0 overflow-y-auto bg-gray-dark-1400 px-0 py-0 !pr-0 lg:min-w-[560px]"
        )}
        showCloseIcon={false}
      >
        <DialogHeader className="gap-2 p-5">
          <DialogTitle className="text-2xl">Purchase IO Credits</DialogTitle>
          <div className="text-base text-gray-dark-1000">
            Unlock faster processing, optimized payments, and streamlined efficiency with IO
            Credits.
          </div>
        </DialogHeader>

        <Formik<PurchaseCreditsFormType>
          initialValues={initialValues}
          onSubmit={async (values) => {
            const newWindow = window.open("", "_blank");
            try {
              const { amount, currency } = values;
              if (newWindow) {
                newWindow.document.body.innerHTML = PAYMENT_LOADING_HTML;
              }
              const response = await topUpUserCredits({
                amount,
                resourceId: "newPurchase",
                paymentMethod: currency === "usdc" ? "crypto" : "fiat"
              });
              if (newWindow) {
                newWindow.location.href = response.paymentLink;
              } else {
                window.open(response.paymentLink, "_blank");
              }

              trackEvent(AnalyticsEventType.PURCHASE_CREDIT_SUBMITTED, {
                amount
              });

              toast({ title: "Payment link generated", variant: "success" });

              setShouldPollBalance(true);

              onClose();
            } catch (e) {
              if (newWindow) {
                newWindow.document.body.innerHTML = "<p>Failed to generate payment link</p>";
              }
              if (e instanceof Error) {
                displayError({
                  title: "ERROR",
                  message: e.message
                });
              }
            }
          }}
          validationSchema={PurchaseCreditsFormSchema}
          validateOnMount={true}
          validateOnChange={true}
        >
          {({ handleSubmit, touched, errors, isValid }) => {
            return (
              <form onSubmit={handleSubmit} className="flex flex-col">
                <PayIcon className="invisible absolute left-0 top-0" />
                <div className="flex flex-col gap-5 border-y border-gray-dark-900 p-5">
                  <div className="flex flex-col gap-2">
                    <Label>
                      Amount <span className="text-blue-light-300">*</span>
                    </Label>
                    <TextField
                      name="amount"
                      placeholder="$0.00"
                      className={cn(
                        "bg-gray-dark-600 text-white placeholder:text-gray-light-1700 focus:border-gray-light-1900 focus:ring-0 focus-visible:ring-0 focus-visible:ring-offset-0",
                        errors.amount && touched.amount
                          ? "border-red-dark-300"
                          : "border-gray-light-1900"
                      )}
                    />
                    <ErrorMessage
                      name={`amount`}
                      component="div"
                      className="subtext-x-small -mt-5 text-red-light-200"
                    />
                    <div className="flex flex-row items-center gap-1">
                      <InfoIcon className="w-[18px] min-w-[18px] text-gray-dark-1500" />
                      <AlertDescription className="text-xs font-medium text-white">
                        1 IO Credit = 1 USD{" "}
                        <span className="text-gray-dark-2200">worth of compute or inference</span>
                      </AlertDescription>
                    </div>

                    <div className="-mb-5 pt-2">
                      <RadioCardField
                        name="currency"
                        className="lg:!grid-cols-1 xl:!grid-cols-2"
                        options={[
                          {
                            label: <PayIcon className="h-[12px]" />,
                            value: "usdc",
                            className: "px-6 py-5 min-h-[96px] items-center flex"
                          },
                          ...(ENV_VARIABLES.IS_FIAT_ENABLED
                            ? [
                                {
                                  label: (
                                    <div className="flex flex-row items-center gap-2">
                                      <div className="rounded-[4px] border border-gray-light-1800 bg-black p-1">
                                        <VisaIcon />
                                      </div>
                                      <span className="subtext-small text-gray-dark-2200">/</span>
                                      <div className="rounded-[4px] border border-gray-light-1800 bg-black p-1">
                                        <MasterCardIcon />
                                      </div>
                                      <span className="subtext-small text-gray-dark-2200">/</span>
                                      <AmericanExpressIcon />
                                    </div>
                                  ),
                                  value: "stripe",
                                  description: (
                                    <div className="label-x-small w-fit rounded-[4px] border border-gray-light-1900 bg-gray-dark-600 px-2 py-1 text-gray-dark-2200">
                                      3% Fee
                                    </div>
                                  )
                                }
                              ]
                            : [])
                        ]}
                      />
                    </div>
                  </div>
                </div>

                <div className="flex flex-col p-5">
                  <ul className="flex flex-col gap-4">
                    <li className="flex flex-row items-center gap-2">
                      <CubeIcon className="h-[26px] text-gray-dark-2200" />
                      <span className="text-sm text-gray-dark-2200">Can be used for IO Cloud</span>
                    </li>
                    <li className="flex flex-row items-center gap-2">
                      <CreditCardPlusIcon className="h-[26px] text-gray-dark-2200" />
                      <span className="text-sm text-gray-dark-2200">
                        Enables fully automated workflows (esp. via API)
                      </span>
                    </li>
                    <li className="flex flex-row items-center gap-2">
                      <PassportIcon className="h-[26px] text-gray-dark-2200" />
                      <span className="text-sm text-gray-dark-2200">
                        No paywall friction during deployments
                      </span>
                    </li>
                  </ul>
                </div>

                <DialogFooter className="flex flex-row !justify-center gap-2 rounded-b-lg px-4 py-4 dark:border-gray-dark-900 dark:bg-black">
                  <div className="flex-1"></div>
                  <Button
                    onClick={onClose}
                    variant="outline"
                    size="sm"
                    data-testid="ConfirmModal_Button"
                  >
                    Cancel
                  </Button>

                  <Button size="sm" className="gap-2" type="submit" disabled={!isValid}>
                    <span>Click to pay</span>
                    <ArrowRightIcon className="h-4 w-4" />
                  </Button>

                  {/* {paymentLink ? <Button
                    type="button"
                    size="sm"
                    onClick={() => {
                      window.open(paymentLink, '_blank');
                      onClose?.();
                    }}
                    className="gap-2"
                  >
                    <span>Click to pay</span>
                    <ArrowRightIcon className="h-4 w-4" />
                  </Button> : <Button
                    type="submit"
                    size="sm"
                    disabled={isSubmitting || !isValid}
                    className="gap-2"
                  >
                    {isSubmitting ? (
                      <div className="mr-2">
                        <Loader className="!h-[12px] !w-[12px]" color="#fff" />
                      </div>
                    ) : null}
                    <span>Generate payment link</span>
                    <ArrowRightIcon className="h-4 w-4" />
                  </Button>} */}
                </DialogFooter>
              </form>
            );
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  );
};

type PurchaseCreditsFormType = {
  amount: number;
  currency: string;
};

const PurchaseCreditsFormSchema = Yup.object().shape({
  amount: Yup.number()
    .min(1, "Minimum amount of credits that can be purchased is 1")
    .required(REQUIRED_MESSAGE)
    .test("max-2-decimals", "Amount cannot have more than 2 decimal places", (value) => {
      if (value === undefined || value === null) return true; // let other validators handle required
      return /^\d+(\.\d{1,2})?$/.test(value.toString());
    }),
  currency: Yup.string().required(REQUIRED_MESSAGE)
});
